import 'dayjs/locale/es';

import { Box, Grid, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { updateSGRWarrantyRequest } from 'lib/api/sgr';
import { UpdateForm } from 'lib/api/sgr/types';
import UpdateWarrantyForm from 'lib/common/Forms/Warranty/BackOffice/Update';
import CustomButton from 'lib/designSystem/Button';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import { showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Calculator from '../Calculator';
import PaymentDocument from '../PaymentDocument';
import ConfirmationDialog from './Components/ConfirmationDialog';

dayjs.locale('es');

type Props = {
  warranty: Warranty;
  setIsEditing: (isEditing: boolean) => void;
  refreshWarranty?: () => void;
};

const UpdateWarranty: React.FC<Props> = ({
  warranty,
  setIsEditing,
  refreshWarranty,
}) => {
  const initialValues = useMemo(() => {
    return {
      tenderId: warranty.tenderId || null,
      type: warranty.type,
      quotedAmount: warranty.quotedAmount,
      quotedCurrency: warranty.quotedCurrency,
      principalRut: warranty.principalRut || null,
      unitCode: warranty.unitCode || null,
      startDate: dayjs(warranty.startDate),
      endDate: dayjs(warranty.endDate),
      description: warranty.description || null,
      fogape: warranty.fogape || null,
      operationCostFixed: warranty.operationCostFixed || 0,
      operationCostVariable: warranty.operationCostVariable || 0,
      discountRateVariable: warranty.discountRateVariable || null,
      discountRateFixed: warranty.discountRateFixed || null,
      decisionEngineId: null,
      runDecisionEngine: false,
      emitCertificate: false,
    };
  }, [warranty]);
  const dispatch = useDispatch();
  const [form, setForm] = useState<UpdateForm>(initialValues);
  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable ?? null,
    fixedRate: warranty.operationCostFixed ?? null,
    variableDiscount: warranty.discountRateVariable ?? null,
    fixedDiscount: warranty.discountRateFixed ?? null,
  });
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = updateSGRWarrantyRequest(dispatch);

  const handleConfirm = async () => {
    const data = {
      ...form,
      operationCostVariable: rateControls.variableRate,
      operationCostFixed: rateControls.fixedRate,
      discountRateVariable: rateControls.variableDiscount,
      discountRateFixed: rateControls.fixedDiscount,
    };
    mutate(
      { warrantyRequestId: warranty.id, data },
      {
        onSuccess: () => {
          setOpen(false);
          setIsEditing(false);
          if (refreshWarranty) refreshWarranty();
          showAlert(dispatch, 'success', 'Garantía actualizada exitosamente');
        },
      },
    );
  };

  const handleOpenConfirmation = () => {
    if (isFormValid()) setOpen(true);
  };

  const isFormValid = () => {
    return (
      form.type &&
      form.quotedAmount &&
      form.quotedCurrency &&
      form.startDate &&
      form.endDate &&
      form.principalRut
    );
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={6}>
            <UpdateWarrantyForm form={form} setForm={setForm} />
          </Grid>

          <Grid item xs={6}>
            {warranty.status === 'ACTIVE' ? (
              <Box sx={{ mb: 2 }}>
                <PaymentDocument warranty={warranty} />
              </Box>
            ) : (
              <Calculator
                amount={form.quotedAmount}
                startDate={form.startDate}
                endDate={form.endDate}
                quotedCurrency={form.quotedCurrency}
                type={form.type}
                businessIdentifier={warranty.businessIdentifier}
                rateControls={rateControls}
                setRateControls={setRateControls}
                useConcentrationRates={false}
                canEditRates={true}
              />
            )}
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
          <CustomButton color="secondary" onClick={() => setIsEditing(false)}>
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleOpenConfirmation}
            disabled={!isFormValid()}
          >
            Guardar Cambios
          </CustomButton>
        </Stack>
      </Box>

      {open && (
        <ConfirmationDialog
          open={open}
          onClose={() => setOpen(false)}
          form={form}
          setForm={setForm}
          rateControls={rateControls}
          originalWarranty={warranty}
          isPending={isPending}
          handleConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default UpdateWarranty;
